import React, { useMemo, useState } from 'react';
import type { PartnerCase } from './PartnerPortal.tsx';
import type { FirmSettings, MessageDefaults } from '../../services/tenantApi.ts';
import { FirmWorkingPreferences } from './FirmWorkingPreferences.tsx';
import { TemplateAccordionRow } from './TemplateAccordionRow.tsx';

/** Legacy/planned ACAT display fields read here but not modelled in shared/factFindTypes.ts's
 *  AcatPerson (no current writer) — see factFindTypes.ts's own note on legacy/dead reads. */
interface LegacyAcatFields {
    approvedLevel?: string;
    assessmentPending?: boolean;
}

const uploadDashStyle = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 13px',
    border: '1.5px dashed var(--bdr)',
    borderRadius: 'var(--r)',
    cursor: 'pointer',
    fontSize: 13,
    color: 'var(--muted)',
    transition: 'border-color .15s',
};

/** Full adviser Settings view from Professional Portal Wireframe.html */
/** The signed-in adviser's real identity, as the portal assembles it from the login + tenant record. */
export interface AdviserSettingsIdentity {
    adviserName: string;
    email?: string;
    firmName: string;
    licenceLine: string;
}

/**
 * Settings.
 *
 * IMPORTANT: this screen is a DESIGN PREVIEW, not a working settings page. Nothing on it saves —
 * there is no endpoint behind any of it — and the two lower sections configure reminder emails,
 * which aren't connected either. It previously compounded that by showing a completely invented
 * adviser ("James Mitchell", a different firm, a different AFSL) as though it were the signed-in
 * user's own details, which is worse than showing nothing: a real adviser could reasonably read it
 * as their record and wonder why it was wrong.
 *
 * So it now shows the REAL signed-in identity, read-only, and says plainly what isn't active yet.
 * The mocked-up rules and templates are left in place because they are a useful preview of the
 * intended design — they're just labelled as such.
 */
export function AdviserSettingsView({
    identity = null,
    firmSettings,
    messageDefaults,
    onSaveFirmSettings,
}: {
    identity?: AdviserSettingsIdentity | null;
    firmSettings?: FirmSettings;
    messageDefaults?: MessageDefaults | null;
    onSaveFirmSettings?: (patch: FirmSettings) => Promise<boolean>;
}) {
    const [reminderMode, setReminderMode] = useState('auto');
    const [firstName = '', ...restOfName] = String(identity?.adviserName || '').trim().split(/\s+/);

    return (
        <>
            <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>Settings</h2>
            <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 14 }}>Your profile, follow-up rules, and client message templates.</p>

            <div
                className="ccm"
                style={{ marginBottom: 20, padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, color: '#854d0e', maxWidth: 900 }}
            >
                <strong>Your details are read-only.</strong> They come from your sign-in and can&rsquo;t be edited
                here yet. Everything else on this page &mdash; the follow-up interval, the two client emails and your
                document links &mdash; saves and takes effect. The accordion at the bottom previews a longer reminder
                sequence that doesn&rsquo;t exist yet.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20, maxWidth: 900 }}>
                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', padding: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 13 }}>Adviser Profile</div>
                    <div className="fg">
                        <label className="fl">First Name</label>
                        <input className="fi" value={firstName} readOnly />
                    </div>
                    <div className="fg">
                        <label className="fl">Last Name</label>
                        <input className="fi" value={restOfName.join(' ')} readOnly />
                    </div>
                    <div className="fg">
                        <label className="fl">Email</label>
                        <input className="fi" value={identity?.email || ''} readOnly />
                    </div>
                    <div className="fg">
                        <label className="fl">Auth Rep / AFSL</label>
                        <input className="fi" value={identity?.licenceLine || ''} readOnly />
                    </div>
                    <div className="fg">
                        <label className="fl">Phone</label>
                        <input className="fi" placeholder="e.g. 02 9123 4567" />
                    </div>
                    <div style={{ borderTop: '1px solid var(--bdr)', margin: '14px 0' }} />
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', marginBottom: 11 }}>FSG & Company Documents</div>
                    <div className="fg">
                        <label className="fl">Financial Services Guide (FSG)</label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <label
                                style={uploadDashStyle}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = 'var(--blue)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = 'var(--bdr)';
                                }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                                Upload PDF
                                <input type="file" accept=".pdf" style={{ display: 'none' }} />
                            </label>
                            <span style={{ fontSize: 12, color: 'var(--muted)' }}>or</span>
                            <input className="fi" style={{ flex: 1 }} placeholder="Paste a link to your FSG" />
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Linked or embedded in the initial invite email sent to clients.</div>
                    </div>
                    <div className="fg">
                        <label className="fl">Company / Practice Profile</label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <label
                                style={uploadDashStyle}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = 'var(--blue)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = 'var(--bdr)';
                                }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                                Upload PDF
                                <input type="file" accept=".pdf" style={{ display: 'none' }} />
                            </label>
                            <span style={{ fontSize: 12, color: 'var(--muted)' }}>or</span>
                            <input className="fi" style={{ flex: 1 }} placeholder="Website / profile link" />
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Displayed on the client&apos;s invitation and profile portal header.</div>
                    </div>
                    <button type="button" disabled title="Saving settings isn't available yet" className="bsm bp">
                        Save Profile
                    </button>
                </div>

                {/* The live controls, shared with the real firm Settings view so the two cannot drift. */}
                <div style={{ gridColumn: '1 / -1' }}>
                    <FirmWorkingPreferences settings={firmSettings} messageDefaults={messageDefaults} onSave={onSaveFirmSettings} />
                </div>

                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', padding: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Follow-up Rules</div>
                    <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 13 }}>Configure how the system handles inactive clients. You can disable auto-sends and manage reminders manually.</p>

                    <div style={{ marginBottom: 13 }}>
                        <label className="fl">Reminder mode</label>
                        <select className="fi" style={{ cursor: 'pointer' }} value={reminderMode} onChange={(e) => setReminderMode(e.target.value)}>
                            <option value="auto">Automatic — system sends emails on my behalf</option>
                            <option value="manual">Manual only — alert me in-app, I&apos;ll reach out myself</option>
                            <option value="off">Off — no reminders or alerts</option>
                        </select>
                    </div>
                    {reminderMode === 'auto' && (
                        <div id="auto-rules">
                            <div className="fur">
                                <div className="ful">First reminder if invite not opened after</div>
                                <select className="fus" defaultValue="5 days">
                                    <option>3 days</option>
                                    <option>5 days</option>
                                    <option>7 days</option>
                                </select>
                            </div>
                            <div className="fur">
                                <div className="ful">Second reminder if still inactive after</div>
                                <select className="fus" defaultValue="10 days">
                                    <option>7 days</option>
                                    <option>10 days</option>
                                    <option>14 days</option>
                                </select>
                            </div>
                            <div className="fur">
                                <div className="ful">Stop auto-reminders after</div>
                                <select className="fus" defaultValue="3 sends">
                                    <option>2 sends</option>
                                    <option>3 sends</option>
                                    <option>5 sends</option>
                                </select>
                            </div>
                        </div>
                    )}
                    <div className="fur">
                        <div className="ful">Alert me if client stalls mid-profile after</div>
                        <select className="fus" defaultValue="2 days">
                            <option>2 days</option>
                            <option>3 days</option>
                            <option>5 days</option>
                            <option>Never</option>
                        </select>
                    </div>
                    <button type="button" disabled title="Saving settings isn't available yet" className="bsm bp" style={{ marginTop: 13 }}>
                        Save Rules
                    </button>
                </div>
            </div>

            <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', overflow: 'hidden', maxWidth: 900 }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--bdr)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--dark)' }}>Communication Templates</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                            These are sent automatically or on-demand. Edit to match your tone. Use{' '}
                            <code style={{ fontSize: 11, background: 'var(--bg2)', padding: '1px 5px', borderRadius: 4 }}>{'{client_name}'}</code>,{' '}
                            <code style={{ fontSize: 11, background: 'var(--bg2)', padding: '1px 5px', borderRadius: 4 }}>{'{adviser_name}'}</code>,{' '}
                            <code style={{ fontSize: 11, background: 'var(--bg2)', padding: '1px 5px', borderRadius: 4 }}>{'{link}'}</code> as placeholders.
                        </div>
                    </div>
                    <button type="button" disabled title="This section is a design preview" className="bsm bg" style={{ fontSize: 12, flexShrink: 0 }}>
                        Reset to Defaults
                    </button>
                </div>

                <div id="tpl-list">
                    <TemplateAccordionRow id={1} dotColor="#3b82f6" name="Initial Invite" meta="Sent when you invite a new client  ·  Trigger: manual">
                        <div className="fg">
                            <label className="fl">Subject line</label>
                            <input className="fi" defaultValue="{adviser_name} has shared a planning tool with you" />
                        </div>
                        <div className="fg">
                            <label className="fl">Message body</label>
                            <textarea className="fi" rows={6} style={{ resize: 'vertical', lineHeight: 1.6 }} defaultValue={`Hi {client_name},

I've set up a personalised planning tool for you through AgedCareWise. It will help us explore your options, understand your situation, and make the most of our time together.

It should take around 15–20 minutes to complete. Please click the link below to get started:

{link}

If you have any questions before you begin, please don't hesitate to reach out.

Kind regards,
{adviser_name}`} />
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10 }}>
                            If you have an FSG, it will be automatically attached or linked in this email. You can upload your FSG in the Adviser Profile section above.
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" disabled title="Saving settings isn't available yet" className="bsm bp">
                                Save Template
                            </button>
                            <button type="button" disabled title="Saving settings isn't available yet" className="bsm bg">
                                Preview Email
                            </button>
                        </div>
                    </TemplateAccordionRow>

                    <TemplateAccordionRow id={2} dotColor="#f59e0b" name="Follow-up Nudge (1st reminder)" meta="Auto-sent if invite not opened after your threshold  ·  Trigger: automatic">
                        <div className="fg">
                            <label className="fl">Subject line</label>
                            <input className="fi" defaultValue="Just a reminder — your planning link from {adviser_name} is still active" />
                        </div>
                        <div className="fg">
                            <label className="fl">Message body</label>
                            <textarea className="fi" rows={5} style={{ resize: 'vertical', lineHeight: 1.6 }} defaultValue={`Hi {client_name},

Just a quick follow-up — your planning tool link is still active. It only takes around 15–20 minutes to complete, and it will help us prepare for our conversation.

{link}

Kind regards,
{adviser_name}`} />
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" disabled title="Saving settings isn't available yet" className="bsm bp">
                                Save Template
                            </button>
                            <button type="button" disabled title="Saving settings isn't available yet" className="bsm bg">
                                Preview Email
                            </button>
                        </div>
                    </TemplateAccordionRow>

                    <TemplateAccordionRow id={3} dotColor="#ef4444" name="Follow-up Nudge (2nd reminder)" meta="Auto-sent after second threshold  ·  Trigger: automatic">
                        <div className="fg">
                            <label className="fl">Subject line</label>
                            <input className="fi" defaultValue="One more reminder from {adviser_name} — your link is still waiting" />
                        </div>
                        <div className="fg">
                            <label className="fl">Message body</label>
                            <textarea className="fi" rows={5} style={{ resize: 'vertical', lineHeight: 1.6 }} defaultValue={`Hi {client_name},

I wanted to follow up one more time. I know life gets busy — whenever you have a spare 15 minutes, the link below is still active.

{link}

Feel free to call me if you'd prefer to go through it together or have any questions.

Kind regards,
{adviser_name}`} />
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" disabled title="Saving settings isn't available yet" className="bsm bp">
                                Save Template
                            </button>
                            <button type="button" disabled title="Saving settings isn't available yet" className="bsm bg">
                                Preview Email
                            </button>
                        </div>
                    </TemplateAccordionRow>

                    <TemplateAccordionRow id={4} dotColor="#10b981" name="Profile Submitted — Confirmation to Client" meta="Sent to client when they submit their profile  ·  Trigger: automatic on submission">
                        <div className="fg">
                            <label className="fl">Subject line</label>
                            <input className="fi" defaultValue="Thanks {client_name} — we've received your information" />
                        </div>
                        <div className="fg">
                            <label className="fl">Message body</label>
                            <textarea className="fi" rows={6} style={{ resize: 'vertical', lineHeight: 1.6 }} defaultValue={`Hi {client_name},

Thank you for completing your profile. I've received all your information and will now review it and prepare for our next conversation.

I'll be in touch shortly. In the meantime, if anything changes or you have questions, please don't hesitate to reach out.

Kind regards,
{adviser_name}`} />
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" disabled title="Saving settings isn't available yet" className="bsm bp">
                                Save Template
                            </button>
                            <button type="button" disabled title="Saving settings isn't available yet" className="bsm bg">
                                Preview Email
                            </button>
                        </div>
                    </TemplateAccordionRow>

                    <TemplateAccordionRow id={5} dotColor="#6366f1" name="Report / Digital SOA Ready — Notification to Client" meta="Sent when you publish a report or Digital SOA for the client  ·  Trigger: manual (on publish)">
                        <div className="fg">
                            <label className="fl">Subject line</label>
                            <input className="fi" defaultValue="Your personalised report from {adviser_name} is ready to view" />
                        </div>
                        <div className="fg">
                            <label className="fl">Message body</label>
                            <textarea className="fi" rows={6} style={{ resize: 'vertical', lineHeight: 1.6 }} defaultValue={`Hi {client_name},

Your personalised report is now ready to view. It summarises the options we've explored and the recommendations I've prepared based on your situation.

You can access it securely here:

{link}

Please review it at your convenience, and feel free to call me if you have any questions or would like to discuss anything.

Kind regards,
{adviser_name}`} />
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" disabled title="Saving settings isn't available yet" className="bsm bp">
                                Save Template
                            </button>
                            <button type="button" disabled title="Saving settings isn't available yet" className="bsm bg">
                                Preview Email
                            </button>
                        </div>
                    </TemplateAccordionRow>
                </div>
            </div>
        </>
    );
}

function computeAgeFromDob(dobStr: string | null | undefined): number | null {
    if (!dobStr) return null;
    const birth = new Date(dobStr);
    if (Number.isNaN(birth.getTime())) return null;
    const age = Math.floor((Date.now() - birth.getTime()) / (365.25 * 86400000));
    return age >= 0 && age < 120 ? age : null;
}

function pctFromTotal(n: number, total: number): string {
    if (!total) return '0%';
    return `${Math.round((n / total) * 100)}%`;
}

function avg(values: number[]): number {
    if (!values.length) return 0;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
}

/** Full Analytics view — KPI row blends demo case data where possible */
export function AdviserAnalyticsView({
    cases = [],
    dataState = 'idle',
}: {
    cases?: PartnerCase[];
    /** Progress of the on-demand fact-find fetch these charts are built from. */
    dataState?: 'idle' | 'loading' | 'loaded' | 'partial';
}) {
    const derived = useMemo(() => {
        const completedStatuses = ['submitted', 'adviser_review', 'soa_draft', 'soa_published'];
        const completed = cases.filter((c) => completedStatuses.includes(c.status));
        const ages = completed.map((c) => computeAgeFromDob(c.factFind?.client?.dob)).filter((v) => v != null);
        const avgAge = ages.length ? avg(ages).toFixed(1) : '—';
        const minA = ages.length ? Math.min(...ages) : null;
        const maxA = ages.length ? Math.max(...ages) : null;
        /*
         * Days from the invite going out to the profile coming back, averaged over the ones that
         * actually came back. Null — rendered as an em dash — when nothing has been submitted yet, or
         * when the dates needed are missing: an average of no observations is not zero, and it is not
         * 4.2 either.
         */
        const completionSpans = completed
            .map((c) => {
                /*
                 * Invite sent -> fact-find complete, which is what the label under this number claims
                 * it measures. A client the adviser set up in person has no invite (inviteSentAt is
                 * null) and is skipped rather than counted as zero days: they were never waited on.
                 */
                const from = c.workflow?.inviteSentAt ? Date.parse(c.workflow.inviteSentAt) : NaN;
                const completedAt = c.workflow?.completedAt ?? c.submissionDate ?? null;
                const to = completedAt ? Date.parse(completedAt) : NaN;
                return Number.isFinite(from) && Number.isFinite(to) && to >= from ? (to - from) / 86_400_000 : null;
            })
            .filter((v): v is number => v != null);
        const avgCompletionDays = completionSpans.length ? avg(completionSpans) : null;

        const soaSent = cases.filter((c) => c.soaStatus === 'published').length;
        const invited = cases.filter((c) => c.status === 'invited').length;
        const started = cases.filter((c) => c.status === 'started').length;
        const total = cases.length;
        const completedN = completed.length;

        const ageBuckets = [
            { key: '60s', label: '60s', count: 0 },
            { key: '70_74', label: '70–74', count: 0 },
            { key: '75_79', label: '75–79', count: 0 },
            { key: '80_84', label: '80–84', count: 0 },
            { key: '85p', label: '85+', count: 0 },
        ];
        ages.forEach((age) => {
            if (age < 70) ageBuckets[0].count += 1;
            else if (age <= 74) ageBuckets[1].count += 1;
            else if (age <= 79) ageBuckets[2].count += 1;
            else if (age <= 84) ageBuckets[3].count += 1;
            else ageBuckets[4].count += 1;
        });
        const maxAgeBucket = Math.max(1, ...ageBuckets.map((b) => b.count));
        const ageDistribution = ageBuckets.map((bucket) => ({
            ...bucket,
            height: Math.max(12, Math.round((bucket.count / maxAgeBucket) * 95)),
        }));

        const goalsByKey: Record<string, string> = {
            home_care: 'Stay at home with support (HCP)',
            aged_care: 'Residential aged care facility',
            retirement_village: 'Retirement village',
            granny_flat: 'Granny flat / with family',
            downsize_home: 'Downsize family home',
            undecided: 'Undecided',
        };
        const goalCounts: Record<string, number> = {};
        completed.forEach((c) => {
            const goals = (c.factFind?.goals || {}) as Record<string, unknown>;
            const keys = Object.keys(goals).filter((k) => goals[k] === true && goalsByKey[k]);
            if (!keys.length) {
                goalCounts.undecided = (goalCounts.undecided || 0) + 1;
                return;
            }
            keys.forEach((k) => {
                goalCounts[k] = (goalCounts[k] || 0) + 1;
            });
        });
        const totalGoalFlags = Object.values(goalCounts).reduce((sum, v) => sum + v, 0) || 1;
        const livingArrangements = Object.entries(goalsByKey).map(([key, label]) => ({
            label,
            count: goalCounts[key] || 0,
            pct: Math.round(((goalCounts[key] || 0) / totalGoalFlags) * 100),
        }));
        /*
         * The most common goal among completed profiles — or an em dash when none has been stated.
         * Sorting a list of zeros returns the first entry, so this used to name "Stay at home with
         * support (HCP)" as the most common goal of a firm with no clients, directly above the words
         * "0% of completed profiles". A ranking of nothing has no winner.
         */
        /*
         * The most common goal — but only when one actually leads.
         *
         * Two problems, both found by the owner. Sorting a list of zeros returns the first entry, so an
         * empty book named a "most common goal" above the words "0% of completed profiles". And a
         * client who chose TWO goals produces a dead heat, where naming either one is an arbitrary
         * pick presented as a finding. Clients routinely want more than one thing — staying home and
         * residential care are both on the table while a family decides — so ties are normal here
         * rather than a curiosity.
         *
         * Where nothing leads, the tile says so, and the Preferred Living Arrangements chart directly
         * below already carries the full breakdown.
         */
        const rankedGoals = [...livingArrangements].sort((a, b) => b.count - a.count);
        const leader = rankedGoals[0];
        const tiedWithLeader = leader?.count ? rankedGoals.filter((g) => g.count === leader.count).length : 0;
        const topGoal =
            !leader?.count
                ? { label: '—', pct: 0 }
                : tiedWithLeader > 1
                  ? { label: `${tiedWithLeader} goals tied`, pct: leader.pct }
                  : leader;

        const careStageCounts = {
            no_assessment: 0,
            acat_pending: 0,
            hcp_1_2: 0,
            hcp_3_4: 0,
            residential: 0,
        };
        completed.forEach((c) => {
            const acat = c.factFind?.careNeeds?.clientAcat as LegacyAcatFields | undefined;
            const level = acat?.approvedLevel;
            if (!level) {
                careStageCounts.no_assessment += 1;
                return;
            }
            if (acat?.assessmentPending) careStageCounts.acat_pending += 1;
            if (String(level).toLowerCase().includes('residential')) careStageCounts.residential += 1;
            else if (String(level).includes('3') || String(level).includes('4')) careStageCounts.hcp_3_4 += 1;
            else careStageCounts.hcp_1_2 += 1;
        });
        const acatRows = [
            { label: 'No assessment yet — planning ahead', count: careStageCounts.no_assessment, color: '#e2e8f0' },
            { label: 'Needs assessment pending', count: careStageCounts.acat_pending, color: '#fcd34d', amb: true },
            { label: 'HCP Level 1–2 approved', count: careStageCounts.hcp_1_2, color: '#93c5fd' },
            { label: 'HCP Level 3–4 approved', count: careStageCounts.hcp_3_4, color: 'var(--blue)' },
            { label: 'Residential care approved / active', count: careStageCounts.residential, color: '#a78bfa', pur: true },
        ].map((row) => ({ ...row, pct: completedN ? Math.round((row.count / completedN) * 100) : 0 }));

        const strategyCases = completed.filter((c) => c.strategySource || c.roomFunding || c.homeDecision || c.strategyUsageMode);
        const strategySourceCounts = strategyCases.reduce(
            (acc, c) => {
                if (c.strategySource === 'manual_control') acc.manual += 1;
                else acc.suggestion += 1;
                return acc;
            },
            { manual: 0, suggestion: 0 }
        );
        const usageCounts = strategyCases.reduce(
            (acc, c) => {
                const mode = c.strategyUsageMode || (c.strategySource === 'manual_control' ? 'manual_only' : 'suggestion_only');
                if (mode === 'hybrid') acc.hybrid += 1;
                else if (mode === 'manual_only') acc.manualOnly += 1;
                else acc.suggestionOnly += 1;
                return acc;
            },
            { manualOnly: 0, suggestionOnly: 0, hybrid: 0 }
        );
        const manualCases = strategyCases.filter((c) => (c.strategyUsageMode || '') === 'manual_only' || (c.strategyUsageMode || '') === 'hybrid' || c.strategySource === 'manual_control');
        const manualAverageStrategies = manualCases.length
            ? avg(manualCases.map((c) => Number(c.manualStrategiesModelled) || Number(c.strategyGenerationCount) || 0)).toFixed(1)
            : '0.0';
        const manualAverageSavedStrategies = manualCases.length
            ? avg(manualCases.map((c) => Number(c.manualStrategiesSaved) || 0)).toFixed(1)
            : '0.0';

        const suggestionCases = strategyCases.filter((c) => c.strategySource === 'suggestion_engine' || c.strategyUsageMode === 'hybrid' || c.strategyUsageMode === 'suggestion_only');
        const suggestionSelectionLabels = {
            optimal_estate: 'Optimal estate value',
            optimal_cashflow: 'Optimal cashflow',
            optimal_age_pension: 'Optimal age pension',
            other_from_full_list: 'Other (from full strategy list)',
        };
        const suggestionSelectionCounts: Record<string, number> = {};
        suggestionCases.forEach((c) => {
            const key = c.suggestionSoaSelection || 'other_from_full_list';
            suggestionSelectionCounts[key] = (suggestionSelectionCounts[key] || 0) + 1;
        });
        const suggestionSelectionRows = Object.entries(suggestionSelectionLabels).map(([key, label]) => ({
            key,
            label,
            count: suggestionSelectionCounts[key] || 0,
        }));

        const homeDecisionLabels = {
            keep_home: 'Keep family home',
            sell_home: 'Sell family home',
            rent_home: 'Rent family home',
            downsize_home: 'Downsize family home',
        };
        const homeDecisionCounts: Record<string, number> = {};
        strategyCases.forEach((c) => {
            const key = c.homeDecision || 'keep_home';
            homeDecisionCounts[key] = (homeDecisionCounts[key] || 0) + 1;
        });
        const commonStrategies = Object.entries(homeDecisionLabels)
            .map(([key, label]) => ({ label, count: homeDecisionCounts[key] || 0 }))
            .sort((a, b) => b.count - a.count);

        const assetSellCounts: Record<string, number> = {};
        strategyCases.forEach((c) => {
            (c.assetSellMix || []).forEach((asset) => {
                assetSellCounts[asset] = (assetSellCounts[asset] || 0) + 1;
            });
        });
        const assetSellRows = Object.entries(assetSellCounts)
            .map(([key, count]) => ({
                label: key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()),
                count,
            }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);

        const fundingStats = strategyCases.reduce(
            (acc, c) => {
                const roomFunding = c.roomFunding || {};
                const room = Number(c.agedCareRoomCost) || 0;
                const radPct = Number(roomFunding.radPct) || 0;
                const dapPct = Number(roomFunding.dapPct) || (100 - radPct);
                acc.totalRoom += room;
                acc.totalRadPct += radPct;
                acc.totalDapPct += dapPct;
                if ((roomFunding.mode || '') === 'full_rad') acc.fullRad += 1;
                if ((roomFunding.mode || '') === 'dap_only') acc.dapOnly += 1;
                if ((roomFunding.mode || '') === 'partial_rad') acc.partial += 1;
                return acc;
            },
            { totalRoom: 0, totalRadPct: 0, totalDapPct: 0, fullRad: 0, dapOnly: 0, partial: 0 }
        );
        return {
            completedN,
            avgAge,
            ageRange: minA != null && maxA != null ? `${minA}–${maxA}` : '—',
            soaSent,
            avgCompletionDays,
            invited,
            started,
            submittedN: completedN,
            total: Math.max(total, 1),
            ageDistribution,
            livingArrangements: livingArrangements
                .filter((row) => row.count > 0)
                .sort((a, b) => b.count - a.count)
                .slice(0, 5),
            topGoal,
            acatRows,
            strategySourceCounts,
            usageCounts,
            strategyCasesN: strategyCases.length,
            manualAverageStrategies,
            manualAverageSavedStrategies,
            manualCasesN: manualCases.length,
            suggestionCasesN: suggestionCases.length,
            suggestionSelectionRows,
            commonStrategies,
            assetSellRows,
            averageRoomCost: strategyCases.length ? Math.round(fundingStats.totalRoom / strategyCases.length) : 0,
            averageRadPct: strategyCases.length ? Math.round(fundingStats.totalRadPct / strategyCases.length) : 0,
            averageDapPct: strategyCases.length ? Math.round(fundingStats.totalDapPct / strategyCases.length) : 0,
            fundingMixCounts: {
                fullRad: fundingStats.fullRad,
                dapOnly: fundingStats.dapOnly,
                partial: fundingStats.partial,
            },
        };
    }, [cases]);

    /*
     * A bar's width, as a share of the whole book.
     *
     * Zero returns 0%, which sounds obvious and was not the case: the old version forced a minimum of
     * 8% so that a small-but-real number stayed visible, and applied it to genuine zeros as well — so
     * an empty funnel drew four bars of differing lengths. With no clients at all it was worse still,
     * dividing by a total of zero and handing CSS "NaN%", after which each bar fell back to the width
     * of its own label. That is how a firm with nothing in it got a funnel that looked like a book of
     * business.
     *
     * The floor still applies to anything above zero, because a single client should not be invisible.
     */
    const pct = (n: number) => {
        if (!derived.total || n <= 0) return '0%';
        return `${Math.max(8, Math.round((n / derived.total) * 100))}%`;
    };

    /**
     * Every chart below reads each client's fact-find, which the client LIST doesn't carry — it is
     * encrypted per client and fetched only when needed (see loadFactFindsForAnalytics, triggered
     * when this tab opens). While that is in flight, or where a file couldn't be read, the charts
     * would otherwise render a confident wall of zeros an adviser could mistake for a finding about
     * their book — so the state is stated plainly instead.
     */
    const realCasesMissingFactFind = cases.filter((c) => c.isReal && !c.factFind?.client?.dob).length;
    const notice =
        dataState === 'loading'
            ? 'Loading your clients&rsquo; details…'
            : realCasesMissingFactFind > 0
              ? `${realCasesMissingFactFind} of your ${cases.length} client files couldn't be read, so they're not counted below.`
              : null;

    return (
        <>
            {notice && (
                <div
                    className="ccm"
                    style={{ marginBottom: 16, padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, color: '#854d0e' }}
                >
                    {dataState === 'loading' ? 'Loading your clients’ details…' : notice}
                </div>
            )}
            <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>Analytics</h2>
            <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 20 }}>Aggregate trends across your client base. Data updates as clients complete their profiles.</p>

            <div className="pp-analytics-kpi">
                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', padding: '16px 18px' }}>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 5 }}>Total Profiles Completed</div>
                    <div style={{ fontSize: 26, fontWeight: 500 }}>{derived.completedN}</div>
                    <div style={{ fontSize: 11, color: 'var(--grn)', marginTop: 4 }}>In current scope</div>
                </div>
                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', padding: '16px 18px' }}>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 5 }}>Average Client Age</div>
                    <div style={{ fontSize: 26, fontWeight: 500 }}>{derived.avgAge}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Range: {derived.ageRange}</div>
                </div>
                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', padding: '16px 18px' }}>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 5 }}>Most Common Goal</div>
                    <div style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.3, marginTop: 4 }}>{derived.topGoal.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                        {derived.topGoal.label === '—'
                            ? 'No goals recorded yet'
                            : `${derived.topGoal.pct}% of completed profiles${derived.topGoal.label.includes('tied') ? ' each' : ''}`}
                    </div>
                </div>
                {/*
                    This said "4.2 days" on a firm with no clients at all, because 4.2 was the
                    hardcoded fallback. The live branch was no better: (started + completed) /
                    completed is a ratio of two counts, printed as a number of days, with no date
                    arithmetic anywhere in it — so it was wrong with data as well as without.
                    It is now the real elapsed time, and an em dash when nothing has been submitted.
                */}
                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', padding: '16px 18px' }}>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 5 }}>Avg. Profile Completion</div>
                    <div style={{ fontSize: 26, fontWeight: 500 }}>
                        {derived.avgCompletionDays == null ? '—' : derived.avgCompletionDays.toFixed(1)}
                        <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--muted)' }}>&nbsp;days</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>From invite to submit</div>
                </div>
            </div>

            <div className="pp-analytics-2col">
                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', padding: 20 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', marginBottom: 4 }}>Pipeline Funnel</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>All-time across your client base (scoped)</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ fontSize: 12, color: 'var(--muted)', width: 90, flexShrink: 0 }}>Invited</div>
                            <div style={{ flex: 1, height: 28, background: '#eff6ff', borderRadius: 4, overflow: 'hidden' }}>
                                {/*
                                    Was hardcoded to 100% — a full bar on an empty book — and then
                                    briefly sized by derived.total while being LABELLED with a
                                    different figure, so the length and the number could disagree.
                                    Both now come from the same expression: whatever this bar says,
                                    is what it is as long as.
                                */}
                                <div
                                    style={{
                                        width: pct(derived.invited + derived.started + derived.submittedN),
                                        height: '100%',
                                        background: 'var(--blue)',
                                        opacity: 0.85,
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '0 10px',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: '#fff',
                                    }}
                                >
                                    {derived.invited + derived.started + derived.submittedN}
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ fontSize: 12, color: 'var(--muted)', width: 90, flexShrink: 0 }}>In progress</div>
                            <div style={{ flex: 1, height: 28, background: '#eff6ff', borderRadius: 4, overflow: 'hidden' }}>
                                <div
                                    style={{
                                        width: pct(derived.started + derived.submittedN),
                                        height: '100%',
                                        background: 'var(--blue)',
                                        opacity: 0.75,
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '0 10px',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: '#fff',
                                    }}
                                >
                                    {derived.started}
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ fontSize: 12, color: 'var(--muted)', width: 90, flexShrink: 0 }}>Submitted</div>
                            <div style={{ flex: 1, height: 28, background: '#eff6ff', borderRadius: 4, overflow: 'hidden' }}>
                                <div
                                    style={{
                                        width: pct(derived.submittedN),
                                        height: '100%',
                                        background: 'var(--blue)',
                                        opacity: 0.9,
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '0 10px',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: '#fff',
                                    }}
                                >
                                    {derived.submittedN}
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ fontSize: 12, color: 'var(--muted)', width: 90, flexShrink: 0 }}>SOA Sent</div>
                            <div style={{ flex: 1, height: 28, background: '#f0fdf4', borderRadius: 4, overflow: 'hidden' }}>
                                <div
                                    style={{
                                        /* The fallback here was '40%' — a green bar reporting
                                           advice delivered by a firm that had delivered none. */
                                        width:
                                            derived.submittedN && derived.soaSent
                                                ? `${Math.max(15, Math.round((derived.soaSent / derived.submittedN) * 100))}%`
                                                : '0%',
                                        height: '100%',
                                        background: 'var(--grn)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '0 10px',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: '#fff',
                                    }}
                                >
                                    {derived.soaSent}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', padding: 20 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', marginBottom: 4 }}>Client Age Distribution</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>Age at time of profile submission</div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 120, paddingBottom: 24, position: 'relative' }}>
                        <div style={{ position: 'absolute', bottom: 24, left: 0, right: 0, borderTop: '1px dashed var(--bdr)' }} />
                        {derived.ageDistribution.map((b) => (
                            <div key={b.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                <div style={{ fontSize: 10, color: 'var(--muted)' }}>{b.count}</div>
                                <div style={{ width: '100%', background: b.height > 50 ? 'var(--blue)' : '#93c5fd', borderRadius: '3px 3px 0 0', height: b.height }} />
                                <div style={{ fontSize: 10, color: 'var(--lite)' }}>{b.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="pp-analytics-2col">
                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', padding: 20 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', marginBottom: 4 }}>Preferred Living Arrangements</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>Client-stated preference at time of profile</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                        {derived.livingArrangements.map((row, idx) => (
                            <div key={row.label}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ fontSize: 12, color: 'var(--dark)' }}>{row.label}</span>
                                    <span style={{ fontSize: 12, fontWeight: 500, color: idx === 0 ? 'var(--blue)' : 'var(--muted)' }}>{row.pct}%</span>
                                </div>
                                <div style={{ height: 7, background: 'var(--bg2)', borderRadius: 4 }}>
                                    <div style={{ width: `${row.pct}%`, height: '100%', background: idx === 0 ? 'var(--blue)' : '#93c5fd', borderRadius: 4 }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', padding: 20 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', marginBottom: 4 }}>Assessment / Support Stage</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>Current stage reported in client profiles</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                        {derived.acatRows.map((row) => (
                            <div key={row.label}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ fontSize: 12, color: 'var(--dark)' }}>{row.label}</span>
                                    <span
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color: row.amb ? 'var(--amb)' : row.pur ? 'var(--pur)' : 'var(--muted)',
                                        }}
                                    >
                                        {row.pct}%
                                    </span>
                                </div>
                                <div style={{ height: 7, background: 'var(--bg2)', borderRadius: 4 }}>
                                    <div style={{ width: `${row.pct}%`, height: '100%', background: row.color, borderRadius: 4 }} />
                                </div>
                            </div>
                        ))}
                    </div>
                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--bdr)', fontSize: 11, color: 'var(--muted)' }}>
                        Based on {derived.completedN || '—'} completed profiles in scope. Clients who skipped this question excluded.
                    </div>
                </div>
            </div>

            <div className="pp-analytics-2col">
                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', padding: 20 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', marginBottom: 4 }}>Strategy Build Behaviour</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>Manual Control versus Suggestion Engine activity</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
                        <div style={{ background: '#eff6ff', borderRadius: 8, padding: 12 }}>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Suggestion Engine cases</div>
                            <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--blue)' }}>{derived.strategySourceCounts.suggestion}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>{pctFromTotal(derived.strategySourceCounts.suggestion, derived.strategyCasesN)}</div>
                        </div>
                        <div style={{ background: '#fff7ed', borderRadius: 8, padding: 12 }}>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Manual Control cases</div>
                            <div style={{ fontSize: 22, fontWeight: 600, color: '#b45309' }}>{derived.strategySourceCounts.manual}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>{pctFromTotal(derived.strategySourceCounts.manual, derived.strategyCasesN)}</div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                        <span className="tag t-ok">Suggestion-only: {derived.usageCounts.suggestionOnly}</span>
                        <span className="tag t-pr">Manual-only: {derived.usageCounts.manualOnly}</span>
                        <span className="tag t-st">Hybrid: {derived.usageCounts.hybrid}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>Average manual strategies modelled per client (manual + hybrid)</div>
                    <div style={{ fontSize: 26, fontWeight: 600, color: 'var(--dark)' }}>{derived.manualAverageStrategies}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>Average manual strategies saved: <strong style={{ color: 'var(--dark)' }}>{derived.manualAverageSavedStrategies}</strong></div>
                </div>

                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', padding: 20 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', marginBottom: 4 }}>Room Funding Mix</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>RAD/DAP patterns from strategy-ready cases</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                        <div style={{ background: '#f0fdf4', borderRadius: 8, padding: 10 }}>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Full RAD</div>
                            <div style={{ fontSize: 19, fontWeight: 600 }}>{derived.fundingMixCounts.fullRad}</div>
                        </div>
                        <div style={{ background: '#eff6ff', borderRadius: 8, padding: 10 }}>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Partial RAD</div>
                            <div style={{ fontSize: 19, fontWeight: 600 }}>{derived.fundingMixCounts.partial}</div>
                        </div>
                        <div style={{ background: '#f8fafc', borderRadius: 8, padding: 10 }}>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>DAP only</div>
                            <div style={{ fontSize: 19, fontWeight: 600 }}>{derived.fundingMixCounts.dapOnly}</div>
                        </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>Average room cost: <strong style={{ color: 'var(--dark)' }}>${derived.averageRoomCost.toLocaleString()}</strong></div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Average RAD %: <strong style={{ color: 'var(--dark)' }}>{derived.averageRadPct}%</strong> · Average DAP %: <strong style={{ color: 'var(--dark)' }}>{derived.averageDapPct}%</strong></div>
                </div>
            </div>

            <div className="pp-analytics-2col">
                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
                    <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--bdr)' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)' }}>Suggestion Engine SOA Selections</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Which recommendation type is generally selected for the SOA</div>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>Selected Suggestion Path</th>
                                <th>Cases</th>
                                <th>Share</th>
                            </tr>
                        </thead>
                        <tbody>
                            {derived.suggestionSelectionRows.map((row) => (
                                <tr key={row.key}>
                                    <td className="tn">{row.label}</td>
                                    <td className="tm">{row.count}</td>
                                    <td className="tm">{pctFromTotal(row.count, derived.suggestionCasesN)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
                    <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--bdr)' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)' }}>Most Common Home Strategies</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Keep, sell, rent or downsize choices in generated strategies</div>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>Strategy</th>
                                <th>Cases</th>
                                <th>Share</th>
                            </tr>
                        </thead>
                        <tbody>
                            {derived.commonStrategies.map((row) => (
                                <tr key={row.label}>
                                    <td className="tn">{row.label}</td>
                                    <td className="tm">{row.count}</td>
                                    <td className="tm">{pctFromTotal(row.count, derived.strategyCasesN)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
                    <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--bdr)' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)' }}>Most Common Assets Sold Down</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Top liquidated assets used to fund RAD/DAP strategy paths</div>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>Asset Type</th>
                                <th>Mentions</th>
                                <th>Share</th>
                            </tr>
                        </thead>
                        <tbody>
                            {derived.assetSellRows.map((row) => (
                                <tr key={row.label}>
                                    <td className="tn">{row.label}</td>
                                    <td className="tm">{row.count}</td>
                                    <td className="tm">{pctFromTotal(row.count, derived.strategyCasesN)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/*
                "Where Clients Stall Most" used to live here: a table of average time per profile
                section, drop-off rates and "most common exit reason". Every figure in it was typed
                into the source — 8.4 minutes on Assets, 34% drop-off, "super balance uncertainty" —
                and it rendered identically whether the firm had two hundred clients or none.
                Nothing in the product measures time-per-section or records why anyone stopped, so
                there was no number it could ever have shown.

                Removed rather than zeroed, because an empty table still promises a measurement that
                does not exist. If this is wanted, it needs the profile to record step timings and
                abandonment first; the card can come back when there is something behind it.
            */}
        </>
    );
}

/** Practice settings — firm + subscription cards from wireframe */
export function PracticeSettingsWireframeView() {
    return (
        <>
            <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 18 }}>Practice Settings</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 840 }}>
                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', padding: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 13 }}>Firm Details</div>
                    <div className="fg">
                        <label className="fl">Practice Name</label>
                        <input className="fi" defaultValue="La Rosa & Partners Pty Ltd" />
                    </div>
                    <div className="fg">
                        <label className="fl">AFSL Number</label>
                        <input className="fi" defaultValue="482910" />
                    </div>
                    <div className="fg">
                        <label className="fl">ABN</label>
                        <input className="fi" defaultValue="45 123 456 789" />
                    </div>
                    <div className="fg">
                        <label className="fl">Licensee / Dealer Group</label>
                        <input className="fi" defaultValue="Independent — Self-licensed" />
                    </div>
                    <button type="button" className="bsm bp">
                        Save
                    </button>
                </div>
                <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', padding: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 13 }}>Subscription</div>
                    <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>Practice Annual Plan</div>
                    <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--dark)', marginBottom: 4 }}>
                        $2,450 <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--muted)' }}>/year</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--grn)', marginBottom: 14 }}>● Active · Renews 12 Mar 2026</div>
                    <div style={{ fontSize: 13, color: 'var(--muted)' }}>Unlimited clients · 6 team members</div>
                </div>
            </div>
        </>
    );
}
